import {
  BATTLE_MODE_COPY,
  type Battle,
  type BattleCopy,
  type BattleRoomCopy,
  type Currency,
  formatAmountWithUnit,
  type StorefrontTerminology,
} from "@loot-os/api-contract";
import type { BattleOutcomeContent } from "@loot-os/design-system/templates/battles/battlesContent";

import { toSeatView } from "../../shared/battleViews.js";

/**
 * The completed stage: who won, and for a seated viewer the claim. The entry was paid when the
 * seat was taken, so nothing is charged here: a winner claims the items, a loser is minted the
 * battle's loser prize, when it carries one, and either can be sold straight back instead (the
 * claim, then every minted unit sold to the house for its value, in one call). Who won and what
 * they took are the store's default copy (`copy`); the claim's words are the platform's.
 */
export function battleOutcome({
  battle,
  viewerEndUserId,
  showProfile,
  currency,
  terms,
  words,
  copy,
  claim,
  sellBack,
  onReplay,
}: {
  battle: Battle;
  viewerEndUserId: string | null | undefined;
  showProfile: (endUserId: string) => void;
  currency: Currency;
  /** The store's terms, which the claim's words name. */
  terms: StorefrontTerminology;
  words: BattleCopy;
  copy: BattleRoomCopy["outcome"];
  claim: { pending: boolean; error: string | null; onClaim: () => void };
  sellBack: { pending: boolean; error: string | null; onSellBack: () => void };
  onReplay: () => void;
}): BattleOutcomeContent {
  const { item, inventory } = terms;
  const format = (cents: number) => formatAmountWithUnit(cents, currency);
  const winner = battle.winnerSeatIndex !== null ? battle.seats[battle.winnerSeatIndex] : undefined;
  const me = battle.me;
  const iWon = me !== null && me.seatIndex === battle.winnerSeatIndex;
  const wonValue = winner?.totalValueCents ?? null;
  const potCents = battle.seats.reduce((sum, seat) => sum + (seat.totalValueCents ?? 0), 0);
  const prize = me !== null && !iWon ? battle.loserPrizeItem : null;
  // What a sell-back credits this seat: the pot for the winner, the prize for a loser.
  const sellBackCents = iWon ? potCents : (prize?.value ?? 0);
  const busy = claim.pending || sellBack.pending;
  // A loser on a battle without a prize has nothing to mint; the claim just closes the seat.
  const claimLabel = iWon ? `Claim ${item.plural}` : prize ? `Claim ${prize.name}` : "Done";

  return {
    eyebrow: BATTLE_MODE_COPY[battle.mode].takesAll,
    headline: winner
      ? {
          winner: toSeatView(winner, viewerEndUserId, showProfile, words),
          verb: iWon ? copy.win : copy.wins,
        }
      : { text: copy.finished },
    summary: copy.summary({
      pulled: wonValue !== null ? format(wonValue) : null,
      pot: format(potCents),
    }),
    // The prize and the claim are the seat's settlement, so their words are the platform's.
    prize: prize
      ? {
          name: prize.name,
          imageUrl: prize.imageUrl,
          note: `Your prize for playing, worth ${format(prize.value)}`,
        }
      : null,
    claim: !me
      ? null
      : me.claimStatus === "claimed"
        ? {
            status: "done",
            message:
              me.soldCents !== null
                ? `Sold back for ${format(me.soldCents)}.`
                : iWon
                  ? `Claimed — the ${item.plural} are in your ${inventory.singular}.`
                  : prize
                    ? `Claimed — the ${prize.name} is in your ${inventory.singular}.`
                    : "Done — nothing to claim on this one.",
          }
        : {
            status: "open",
            claim: {
              label: claim.pending ? "Claiming…" : claimLabel,
              loading: claim.pending,
              onClaim: claim.onClaim,
            },
            sellBack:
              sellBackCents > 0
                ? {
                    label: sellBack.pending
                      ? "Selling back…"
                      : `Sell back · ${format(sellBackCents)}`,
                    loading: sellBack.pending,
                    onSellBack: sellBack.onSellBack,
                  }
                : null,
            disabled: busy,
            note: `Your entry of ${format(me.costCents)} was paid when you took your seat; nothing more is charged.${
              iWon
                ? ` Claim the ${item.plural} to add them to your ${inventory.singular}`
                : prize
                  ? ` Claim the ${prize.name} to add it to your ${inventory.singular}`
                  : " There is nothing to claim"
            }${
              sellBackCents > 0
                ? `, or sell ${iWon ? "them" : "it"} straight back for ${format(sellBackCents)}`
                : ""
            }. You can't open or join anything else until you decide.`,
          },
    error: claim.error ?? sellBack.error,
    replay: { label: copy.replay, onReplay },
    back: { label: copy.back, href: "/battles" },
  };
}
